import "twin.macro";
import tw from "twin.macro";

import { useState } from "react";
import { useForm } from "react-hook-form";

import { Add } from "@mui/icons-material";
import { Alert, MenuItem } from "@mui/material";

import _ from "lodash";

import {
  useBeaconRanges,
  useCreateBeaconRange,
  useDeleteBeaconRange,
  useUpdateBeaconRange,
} from "@features/beacons/data/beaconRangesQueries";
import { usePurchaseOrder } from "@features/purchaseOrders/context/PurchaseOrderContext";
import { LoadingButton } from "@features/ui";
import { BeaconRange } from "@models/BeaconRange";
import { PurchaseOrderVariantItem } from "@models/PurchaseOrderVariant";
import { ControlledTextInput } from "@utils/forms";

import { OpaqueCard } from "../../../components/StyledComponents";
import OptionsMenu from "../../../components/Utility/OptionsMenu";

type RangeProps = {
  range?: BeaconRange;
  purchaseOrderVariantId: string;
  handleDone?: () => void;
};

const Range = ({ range, purchaseOrderVariantId, handleDone }: RangeProps) => {
  const [editing, setEditing] = useState(false);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      minBeaconUidHex: range?.minBeaconUidHex || "",
      maxBeaconUidHex: range?.maxBeaconUidHex || "",
    },
  });
  const createMutation = useCreateBeaconRange(purchaseOrderVariantId);
  const updateMutation = useUpdateBeaconRange(purchaseOrderVariantId);
  const deleteMutation = useDeleteBeaconRange(purchaseOrderVariantId);

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  const { error, reset } =
    [createMutation, updateMutation, deleteMutation].find((m) => m.error) || {};

  const onSubmit = handleSubmit(async (data) => {
    if (range) {
      await updateMutation.mutateAsync({ ...data, id: range.id });
    } else {
      await createMutation.mutateAsync(data);
    }
    setEditing(false);
    handleDone?.();
  });

  const handleDelete = () => range && deleteMutation.mutateAsync(range.id);

  const reg = (name) => ({
    name,
    control,
    rules: {
      required: "Required",
      pattern: {
        value: /^[0-9a-f]+$/i,
        message: "Must be a valid hex number",
      },
    },
  });

  if (editing || !range?.id)
    return (
      <div tw="p-2 rounded bg-neutral-100">
        <div
          css={[
            tw`flex items-center w-full gap-2 `,
            !range?.id && tw`bg-blue-50`,
          ]}
        >
          <ControlledTextInput
            label="Min UID"
            tw="min-w-40"
            {...reg("minBeaconUidHex")}
          />
          <ControlledTextInput
            label="Max UID"
            tw="min-w-40"
            {...reg("maxBeaconUidHex")}
          />
          <LoadingButton
            variant="contained"
            onClick={onSubmit}
            loading={isPending}
          >
            Save
          </LoadingButton>
        </div>
        {error && (
          <Alert severity="error" tw="mt-2 whitespace-pre-wrap" onClose={reset}>
            {error?.message}
          </Alert>
        )}
      </div>
    );

  return (
    <div tw="flex items-center rounded bg-neutral-50 pl-3 py-1 border border-neutral-50 hover:border-neutral-200 cursor-pointer">
      <div>
        {range.minBeaconUidHex}
        {" — "}
        {range.maxBeaconUidHex}
      </div>
      <OptionsMenu tw="mx-1">
        <MenuItem onClick={() => setEditing(true)}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </OptionsMenu>
    </div>
  );
};

const VariantRanges = ({ variant }: { variant: PurchaseOrderVariantItem }) => {
  const { data: ranges = [], error } = useBeaconRanges(variant.id);
  const [hasDraft, setHasDraft] = useState(false);

  if (error) {
    return (
      <Alert severity="error" tw="mt-2 whitespace-pre-wrap">
        {error?.message}
      </Alert>
    );
  }

  return (
    <div>
      <div tw="flex items-start gap-4">
        <div tw="w-40 flex-none">{variant.variant.variantSku}</div>
        <div tw="flex grow flex-wrap items-start gap-3">
          {ranges.map((range, i) => (
            <Range
              key={range.id || `draft${i}`}
              range={range}
              purchaseOrderVariantId={variant.id}
            />
          ))}
          {hasDraft && (
            <Range
              handleDone={() => setHasDraft(false)}
              purchaseOrderVariantId={variant.id}
            />
          )}
          {!hasDraft && (
            <LoadingButton
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setHasDraft(true)}
            >
              New Range
            </LoadingButton>
          )}
        </div>
      </div>
    </div>
  );
};

const BeaconRangesTable = (props) => {
  const { purchaseOrderVariants } = usePurchaseOrder();
  const nonSetupFeeVariants = _(purchaseOrderVariants)
    .filter((variant) => !variant.isSetupFee)
    .uniqBy((variant) => variant.variant.variantSku)
    .sortBy((variant) => variant.variant.variantSku)
    .value();
  return (
    <OpaqueCard tw="space-y-4" {...props}>
      {nonSetupFeeVariants.map((variant) => (
        <VariantRanges key={variant.id} variant={variant} />
      ))}
    </OpaqueCard>
  );
};

export default BeaconRangesTable;
